import { useEffect, useRef, useState, FormEvent } from "react";
import { messagesApi, Message, VesselMessage } from "../api/messages";
import { vesselsApi, Vessel } from "../api/vessels";

// Unified thread item for the chat view
type ThreadItem =
  | {
      kind: "shore";
      id: string;
      subject: string;
      body: string;
      senderName: string | null;
      senderEmail: string;
      priority: "normal" | "urgent";
      createdAt: string;
    }
  | {
      kind: "vessel";
      id: string;
      subject: string;
      body: string;
      senderName: string;
      senderEmail: string;
      priority: "normal" | "urgent";
      createdAt: string;
      readAt: string | null;
    };

interface VesselConvo {
  vessel: Vessel;
  lastAt: string;
  lastPreview: string;
  unread: number;
}

interface NewMsgForm {
  vesselId: string;
  subject: string;
  body: string;
  priority: "normal" | "urgent";
}

const EMPTY_NEW: NewMsgForm = { vesselId: "", subject: "", body: "", priority: "normal" };

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}

export default function MessagesPage() {
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [sentMessages, setSentMessages] = useState<Message[]>([]);
  const [vesselMessages, setVesselMessages] = useState<VesselMessage[]>([]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composeBody, setComposeBody] = useState("");
  const [composeUrgent, setComposeUrgent] = useState(false);
  const [sending, setSending] = useState(false);

  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState<NewMsgForm>(EMPTY_NEW);
  const [newSubmitting, setNewSubmitting] = useState(false);
  const [newError, setNewError] = useState<string | null>(null);

  const [toast, setToast] = useState<{ msg: string; kind: "success" | "error" } | null>(null);

  const threadBottomRef = useRef<HTMLDivElement>(null);

  function showToast(msg: string, kind: "success" | "error" = "success") {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3000);
  }

  async function loadAll() {
    const [v, sm, vm] = await Promise.all([
      vesselsApi.list(),
      messagesApi.list(),
      messagesApi.listVesselMessages(),
    ]);
    setVessels(v);
    setSentMessages(sm.filter((m) => m.vessel_id !== null));
    setVesselMessages(vm);
  }

  useEffect(() => {
    loadAll().catch(console.error);
  }, []);

  // Scroll to bottom of thread when selection changes or new messages arrive
  useEffect(() => {
    threadBottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [selectedId, sentMessages.length, vesselMessages.length]);

  // Build sidebar conversation list: vessels with at least one message
  const conversations: VesselConvo[] = vessels
    .map((vessel) => {
      const shore = sentMessages.filter((m) => m.vessel_id === vessel.id);
      const crew = vesselMessages.filter((m) => m.vessel_id === vessel.id);
      if (shore.length === 0 && crew.length === 0) return null;

      const allTimes = [
        ...shore.map((m) => m.created_at),
        ...crew.map((m) => m.created_at),
      ].sort().reverse();
      const lastAt = allTimes[0];

      const lastShore = shore.sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      const lastCrew = crew.sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      const lastMsg =
        !lastCrew || (lastShore && lastShore.created_at > lastCrew.created_at)
          ? `You: ${lastShore?.body ?? ""}`
          : `Vessel: ${lastCrew.body}`;

      const unread = crew.filter((m) => !m.read_at).length;

      return { vessel, lastAt, lastPreview: lastMsg, unread } as VesselConvo;
    })
    .filter((c): c is VesselConvo => c !== null)
    .sort((a, b) => b.lastAt.localeCompare(a.lastAt));

  // Build thread for selected vessel
  const thread: ThreadItem[] = selectedId
    ? [
        ...sentMessages
          .filter((m) => m.vessel_id === selectedId)
          .map((m): ThreadItem => ({
            kind: "shore",
            id: m.id,
            subject: m.subject,
            body: m.body,
            senderName: m.sender_name,
            senderEmail: m.sender_email,
            priority: m.priority,
            createdAt: m.created_at,
          })),
        ...vesselMessages
          .filter((m) => m.vessel_id === selectedId)
          .map((m): ThreadItem => ({
            kind: "vessel",
            id: m.id,
            subject: m.subject,
            body: m.body,
            senderName: m.sender_name,
            senderEmail: m.sender_email,
            priority: m.priority,
            createdAt: m.created_at,
            readAt: m.read_at,
          })),
      ].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    : [];

  const selectedVessel = vessels.find((v) => v.id === selectedId) ?? null;

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    if (!selectedId || !composeBody.trim()) return;
    setSending(true);
    try {
      await messagesApi.send({
        vesselId: selectedId,
        subject: `Shore message — ${selectedVessel?.name ?? ""}`,
        body: composeBody.trim(),
        priority: composeUrgent ? "urgent" : "normal",
      });
      setComposeBody("");
      setComposeUrgent(false);
      await loadAll();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "Send failed", "error");
    } finally {
      setSending(false);
    }
  }

  async function handleNewMessage(e: FormEvent) {
    e.preventDefault();
    if (!newForm.vesselId || !newForm.subject || !newForm.body) {
      setNewError("All fields are required");
      return;
    }
    setNewError(null);
    setNewSubmitting(true);
    try {
      await messagesApi.send({
        vesselId: newForm.vesselId,
        subject: newForm.subject,
        body: newForm.body,
        priority: newForm.priority,
      });
      setShowNewModal(false);
      setNewForm(EMPTY_NEW);
      await loadAll();
      setSelectedId(newForm.vesselId);
      showToast("Message sent — vessel contact notified by email");
    } catch (err: unknown) {
      setNewError(err instanceof Error ? err.message : "Send failed");
    } finally {
      setNewSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Page header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
          <p className="text-sm text-gray-500 mt-0.5">Shore ↔ vessel communication</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="rounded-lg bg-navy-700 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 transition-colors"
        >
          New Message
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 rounded-lg text-white text-sm px-4 py-2 shadow-lg ${
            toast.kind === "error" ? "bg-red-600" : "bg-green-600"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* ── Left panel: vessel list ─────────────────────────────────── */}
      <div className="w-1/3 border-r border-gray-200 flex flex-col bg-white">
        <div className="px-4 py-4 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Conversations</h2>
          <p className="text-xs text-gray-400 mt-0.5">Vessel messaging log</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-gray-400">
              No active conversations.<br />
              Use "New Message" above to start one.
            </div>
          ) : (
            conversations.map(({ vessel, lastAt, lastPreview, unread }) => (
              <button
                key={vessel.id}
                onClick={() => setSelectedId(vessel.id)}
                className={`w-full text-left px-4 py-3.5 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                  selectedId === vessel.id ? "bg-navy-50 border-l-2 border-l-navy-600" : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900 truncate">
                        {vessel.name}
                      </span>
                      {unread > 0 && (
                        <span className="shrink-0 rounded-full bg-navy-600 text-white text-xs font-bold px-1.5 py-0.5 leading-none">
                          {unread}
                        </span>
                      )}
                    </div>
                    {vessel.imo_number && (
                      <p className="text-xs text-gray-400">IMO {vessel.imo_number}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{lastPreview}</p>
                  </div>
                  <span className="shrink-0 text-xs text-gray-400 mt-0.5">{formatTime(lastAt)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Right panel: thread ────────────────────────────────────── */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {!selectedVessel ? (
          <div className="flex-1 flex items-center justify-center text-center">
            <div>
              <div className="text-4xl mb-3">⚓</div>
              <p className="text-sm font-medium text-gray-500">Select a vessel to view messages</p>
              <p className="text-xs text-gray-400 mt-1">
                {conversations.length} active conversation{conversations.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Thread header */}
            <div className="px-6 py-4 bg-white border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedVessel.name}</h2>
                  <p className="text-xs text-gray-500">
                    {[
                      selectedVessel.vessel_type,
                      selectedVessel.flag && `Flag: ${selectedVessel.flag}`,
                      selectedVessel.imo_number && `IMO ${selectedVessel.imo_number}`,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
              </div>
            </div>

            {/* Thread messages */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {thread.length === 0 && (
                <p className="text-center text-sm text-gray-400 py-8">No messages yet</p>
              )}
              {thread.map((item) =>
                item.kind === "shore" ? (
                  // Shore message — right, blue
                  <div key={item.id} className="flex justify-end">
                    <div
                      className={`max-w-[70%] rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm ${
                        item.priority === "urgent"
                          ? "bg-navy-700 border-2 border-red-400"
                          : "bg-navy-700"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-navy-200 font-medium">
                          {item.senderName ?? item.senderEmail}
                        </span>
                        {item.priority === "urgent" && (
                          <span className="text-xs font-bold text-red-300 uppercase tracking-wide">
                            Urgent
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white whitespace-pre-wrap">{item.body}</p>
                      <p className="text-xs text-navy-300 mt-1.5 text-right">
                        {formatTime(item.createdAt)}
                      </p>
                    </div>
                  </div>
                ) : (
                  // Vessel reply — left, gray
                  <div key={item.id} className="flex justify-start">
                    <div
                      className={`max-w-[70%] rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm ${
                        item.priority === "urgent"
                          ? "bg-white border-2 border-red-400"
                          : "bg-white border border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-600 font-medium">{item.senderName}</span>
                        {item.priority === "urgent" && (
                          <span className="text-xs font-bold text-red-600 uppercase tracking-wide">
                            Urgent
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{item.body}</p>
                      <p className="text-xs text-gray-400 mt-1.5">{formatTime(item.createdAt)}</p>
                    </div>
                  </div>
                )
              )}
              <div ref={threadBottomRef} />
            </div>

            {/* Compose box */}
            <form
              onSubmit={handleSend}
              className="shrink-0 bg-white border-t border-gray-200 px-6 py-4"
            >
              <textarea
                rows={3}
                value={composeBody}
                onChange={(e) => setComposeBody(e.target.value)}
                placeholder={`Message to ${selectedVessel.name}…`}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 resize-none"
              />
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={composeUrgent}
                    onChange={(e) => setComposeUrgent(e.target.checked)}
                    className="accent-red-600 h-4 w-4"
                  />
                  <span className={composeUrgent ? "text-red-600 font-semibold" : "text-gray-600"}>
                    Urgent
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={sending || !composeBody.trim()}
                  className="rounded-lg bg-navy-700 px-5 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {sending ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      {/* ── New message modal ──────────────────────────────────────── */}
      {showNewModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
            <h2 className="text-lg font-semibold mb-4">New message to vessel</h2>
            <form onSubmit={handleNewMessage} className="space-y-3">
              {newError && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {newError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vessel</label>
                <select
                  required
                  value={newForm.vesselId}
                  onChange={(e) => setNewForm((f) => ({ ...f, vesselId: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                >
                  <option value="">Select a vessel…</option>
                  {vessels.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                      {v.contact_email ? ` — ${v.contact_email}` : " (no contact email)"}
                    </option>
                  ))}
                </select>
                {newForm.vesselId &&
                  !vessels.find((v) => v.id === newForm.vesselId)?.contact_email && (
                    <p className="text-xs text-yellow-600 mt-1">
                      No contact email — message will be saved but no notification sent.
                    </p>
                  )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  required
                  value={newForm.subject}
                  onChange={(e) => setNewForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  required
                  rows={4}
                  value={newForm.body}
                  onChange={(e) => setNewForm((f) => ({ ...f, body: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                />
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700">Priority:</span>
                {(["normal", "urgent"] as const).map((p) => (
                  <label key={p} className="flex items-center gap-1.5 text-sm cursor-pointer">
                    <input
                      type="radio"
                      value={p}
                      checked={newForm.priority === p}
                      onChange={() => setNewForm((f) => ({ ...f, priority: p }))}
                      className="accent-navy-700"
                    />
                    <span className={p === "urgent" ? "text-red-600 font-medium" : "text-gray-600"}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={newSubmitting}
                  className="flex-1 rounded-lg bg-navy-700 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {newSubmitting ? "Sending…" : "Send message"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewModal(false);
                    setNewForm(EMPTY_NEW);
                    setNewError(null);
                  }}
                  className="flex-1 rounded-lg border border-gray-300 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
